import React, { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from '../../../utilities/i18n';
import { Table, Button } from '@adc/parallax-component-library';
import DynamicTable, {
  CheckboxFilterSection,
  ColumnConfig,
} from '../../table/DynamicTable';
import { AccountWithRoleAssignments, Role } from '../../../models';
import RoleAssignmentsModal from '../../modal/RoleAssignmentsModal';

interface RoleAssignmentsTableProps {
  accountsWithRoleAssignments: AccountWithRoleAssignments[];
  roles: Role[];
  isLoading: boolean;
  isError: boolean;
  onSaveRoleChanges: (
    account: AccountWithRoleAssignments,
    addRoleKeys: string[],
    removeRoleKeys: string[],
  ) => Promise<void>;
  isSavingRoleChanges?: boolean;
  headerAction?: React.ReactElement;
}

const getAccountFullName = (account: AccountWithRoleAssignments) => {
  return `${account.firstName} ${account.lastName}`;
};

const getAccountRoleNames = (account: AccountWithRoleAssignments) => {
  return account.roleAssignments
    .map((roleAssignment) => roleAssignment.role.name)
    .join(', ');
};

/**
 * Table for displaying user accounts with role assignments
 * @param {AccountWithRoleAssignments[]} accountsWithRoleAssignments - Accounts with role assignments
 * @param {Role[]} roles - Available roles, used for filtering and for role assignment
 * @param {boolean} isLoading - Data loading state
 * @param {boolean} isError - Data error state
 * @returns {React.JSX.Element}
 */
export default function RoleAssignmentsTable({
  accountsWithRoleAssignments,
  roles,
  isLoading,
  isError,
  onSaveRoleChanges,
  isSavingRoleChanges = false,
  headerAction,
}: RoleAssignmentsTableProps) {
  const [filteredAccounts, setFilteredAccounts] = useState<
    AccountWithRoleAssignments[]
  >([]);
  const [selectedAccount, setSelectedAccount] =
    useState<AccountWithRoleAssignments | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const initialCheckboxFilters = useMemo(() => {
    const items = roles.map((role) => ({
      id: role.key,
      label: role.name,
      value: false,
    }));

    return [
      {
        id: 'role',
        label: i18n.t('Page_RoleAssignments.roleAssignments'),
        items,
      },
    ];
  }, [roles]);

  const [checkboxFilters, setCheckboxFilters] = useState<
    CheckboxFilterSection[]
  >([]);

  useEffect(() => {
    let filtered = accountsWithRoleAssignments;

    checkboxFilters.forEach((filter) => {
      const activeFilterValues = filter.items
        .filter((item) => item.value)
        .map((item) => item.id);

      if (activeFilterValues.length > 0 && filter.id === 'role') {
        filtered = filtered.filter((account) => {
          const hasNoRoles = account.roleAssignments.length === 0;
          const hasSelectedRoles = account.roleAssignments.some(
            (roleAssignment) =>
              activeFilterValues.includes(roleAssignment.role.key),
          );

          return (
            (activeFilterValues.includes('unassigned') && hasNoRoles) ||
            hasSelectedRoles
          );
        });
      }
    });

    setFilteredAccounts(filtered);
  }, [checkboxFilters, accountsWithRoleAssignments]);

  useEffect(() => {
    setCheckboxFilters(initialCheckboxFilters);
  }, [initialCheckboxFilters]);

  const columnData: ColumnConfig[] = useMemo(
    () => [
      {
        label: i18n.t('Page_RoleAssignments.name'),
        dataKey: 'name',
        getSortValue: (account: AccountWithRoleAssignments) =>
          getAccountFullName(account),
      },
      { label: i18n.t('Account.email'), dataKey: 'email' },
      {
        label: i18n.t('Page_RoleAssignments.roleAssignments'),
        dataKey: 'roles',
        getSortValue: (account: AccountWithRoleAssignments) =>
          getAccountRoleNames(account),
      },
      { label: '', dataKey: 'action', isSortable: false },
    ],
    [],
  );

  const handleFiltersReset = useCallback(() => {
    setCheckboxFilters((prevFilters) =>
      prevFilters.map((filter) => ({
        ...filter,
        items: filter.items.map((item) => ({ ...item, value: false })),
      })),
    );
  }, []);

  const cellRenderer = (
    dataKey: string,
    account: AccountWithRoleAssignments,
  ) => {
    if (dataKey === 'name') {
      return (
        <Table.TextCell key={dataKey}>
          {getAccountFullName(account)}
        </Table.TextCell>
      );
    } else if (dataKey === 'roles') {
      return (
        <Table.TextCell key={dataKey}>
          {getAccountRoleNames(account)}
        </Table.TextCell>
      );
    } else if (dataKey === 'action') {
      return (
        <Table.TextCell key={dataKey}>
          <Button
            variant="tertiary"
            size="small"
            label={i18n.t('Page_RoleAssignments.manageRoleAssignments')}
            onPress={() => {
              if (isSavingRoleChanges) {
                return;
              }

              setSelectedAccount(account);
              setIsEditModalOpen(true);
            }}
            singleLine
            disabled={isError || isSavingRoleChanges || roles.length === 0}
            testID={`edit-roles-${account.key}`}
          />
        </Table.TextCell>
      );
    } else {
      return <Table.TextCell key={dataKey}>{account[dataKey]}</Table.TextCell>;
    }
  };

  return (
    <>
      <DynamicTable
        testID="account-assignments-table"
        isLoading={isLoading}
        hasLoadingError={isError}
        description={i18n.t('Page_RoleAssignments.table.description')}
        columnData={columnData}
        cellRenderer={cellRenderer}
        rowData={filteredAccounts}
        enableFiltering={true}
        checkboxFilters={checkboxFilters}
        onFiltersChange={setCheckboxFilters}
        onFiltersReset={handleFiltersReset}
        childrenPlacement="start"
        toolbarProps={{ justifyContent: 'space-between' }}
        showCard={false}
      >
        {headerAction}
      </DynamicTable>

      {selectedAccount ? (
        <RoleAssignmentsModal
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedAccount(null);
          }}
          selectedAccount={selectedAccount}
          availableRoles={roles}
          isSaving={isSavingRoleChanges}
          onSave={(addRoleKeys, removeRoleKeys) => {
            if (isSavingRoleChanges) {
              return Promise.resolve();
            }

            return onSaveRoleChanges(
              selectedAccount,
              addRoleKeys,
              removeRoleKeys,
            );
          }}
        />
      ) : null}
    </>
  );
}
